import { useRef, useState } from 'react';
import { Camera, Upload, AlertTriangle, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase, uploadFile } from '../../lib/supabase';
import { Modal } from './Modal';

// "Scan document" — the owner takes a photo of (or picks a file for) an MOT/KTEO
// certificate, insurance document, service invoice, road tax receipt or registration
// document. The scan-document edge function reads it and SUGGESTS the details; the
// owner always sees and can correct every field before anything is saved. Nothing is
// ever saved automatically.
//
// Each type saves into the same table, with the same columns, as that type's normal
// "Add" form on the vehicle page, and also adds a row to the Documents list so the
// scan shows up on the Documents page as well.

const MAX_MB = 10;

const TYPES = {
  mot: {
    label: 'MOT / KTEO certificate',
    table: 'mechanical_reports', urlField: 'document_url', docType: 'MOT/KTEO',
    fields: [
      ['report_date', 'Report Date', 'date'],
      ['next_due_date', 'Next Due Date', 'date'],
      ['garage_name', 'Garage / Test Centre', 'text'],
      ['result', 'Result *', 'select', ['Passed', 'Failed', 'Needs Repair']],
      ['cost', 'Cost (€)', 'number'],
      ['notes', 'Notes (advisories etc.)', 'textarea'],
    ],
  },
  insurance: {
    label: 'Insurance document',
    table: 'insurance_records', urlField: 'document_url', docType: 'Insurance',
    fields: [
      ['insurance_company', 'Insurance Company', 'text'],
      ['policy_number', 'Policy Number', 'text'],
      ['start_date', 'Start Date', 'date'],
      ['expiry_date', 'Expiry Date *', 'date'],
      ['cost', 'Cost (€)', 'number'],
      ['insurance_type', 'Insurance Type', 'select', ['Third Party', 'Third Party Fire & Theft', 'Full Comprehensive', 'Other']],
      ['insured_amount', 'Insured Amount (€)', 'number', null, f => f.insurance_type === 'Full Comprehensive'],
    ],
  },
  service: {
    label: 'Service invoice / receipt',
    table: 'service_records', urlField: 'invoice_url', docType: 'Invoice',
    fields: [
      ['service_date', 'Service Date', 'date'],
      ['mileage_at_service', 'Mileage at Service', 'number'],
      ['garage_name', 'Garage Name', 'text'],
      ['cost', 'Cost (€)', 'number'],
      ['next_service_date', 'Next Service Date', 'date'],
      ['next_service_mileage', 'Next Service Mileage', 'number'],
      ['work_carried_out', 'Work Carried Out', 'textarea'],
      ['parts_replaced', 'Parts Replaced', 'textarea'],
    ],
  },
  road_tax: {
    label: 'Road tax receipt',
    table: 'road_tax_records', urlField: 'receipt_url', docType: 'Road Tax',
    fields: [
      ['payment_date', 'Payment Date', 'date'],
      ['expiry_date', 'Expiry Date *', 'date'],
      ['amount_paid', 'Amount Paid (€)', 'number'],
    ],
  },
  registration: {
    label: 'Registration / logbook document',
    table: null, docType: 'Registration',
    fields: [
      ['vin', 'VIN / Chassis Number', 'text'],
      ['make', 'Make', 'text'],
      ['model', 'Model', 'text'],
      ['colour', 'Colour', 'text'],
      ['first_registration_date', 'First Registration Date', 'date'],
    ],
  },
  other: {
    label: 'Other document',
    table: null, docType: 'Other',
    fields: [
      ['title', 'Document Name *', 'text'],
      ['document_date', 'Document Date', 'date'],
      ['supplier', 'From / Supplier', 'text'],
      ['total_amount', 'Amount (€)', 'number'],
      ['summary', 'Notes', 'textarea'],
    ],
  },
};

const normReg = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
const int = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

const PAYLOAD = {
  mot: (f, url) => ({
    report_date: f.report_date || null, next_due_date: f.next_due_date || null,
    garage_name: f.garage_name || null, mechanic_name: null, result: f.result,
    cost: num(f.cost), document_url: url, notes: f.notes || null,
  }),
  insurance: (f, url) => ({
    insurance_company: f.insurance_company || null, policy_number: f.policy_number || null,
    start_date: f.start_date || null, expiry_date: f.expiry_date, cost: num(f.cost),
    insurance_type: f.insurance_type || null,
    insured_amount: f.insurance_type === 'Full Comprehensive' ? num(f.insured_amount) : null,
    document_url: url, notes: null,
  }),
  service: (f, url) => ({
    service_date: f.service_date || null, mileage_at_service: int(f.mileage_at_service),
    garage_name: f.garage_name || null, work_carried_out: f.work_carried_out || null,
    parts_replaced: f.parts_replaced || null, cost: num(f.cost), invoice_url: url,
    next_service_date: f.next_service_date || null, next_service_mileage: int(f.next_service_mileage),
    notes: null,
  }),
  road_tax: (f, url) => ({
    payment_date: f.payment_date || null, expiry_date: f.expiry_date,
    amount_paid: num(f.amount_paid), receipt_url: url, notes: null,
  }),
};

const docName = (type, f) => {
  switch (type) {
    case 'mot': return `MOT/KTEO${f.report_date ? ' — ' + f.report_date : ''}`;
    case 'insurance': return `Insurance${f.insurance_company ? ' — ' + f.insurance_company : ''}${f.expiry_date ? ' (to ' + f.expiry_date + ')' : ''}`;
    case 'service': return `Service invoice${f.garage_name ? ' — ' + f.garage_name : f.service_date ? ' — ' + f.service_date : ''}`;
    case 'road_tax': return `Road tax${f.expiry_date ? ' — expires ' + f.expiry_date : ''}`;
    case 'registration': return 'Registration document';
    default: return f.title || 'Scanned document';
  }
};

const docNotes = (type, f) => {
  if (type === 'registration') {
    return [
      f.vin && `VIN: ${f.vin}`, f.make && `Make: ${f.make}`, f.model && `Model: ${f.model}`,
      f.colour && `Colour: ${f.colour}`, f.first_registration_date && `First registered: ${f.first_registration_date}`,
    ].filter(Boolean).join('\n') || null;
  }
  if (type === 'other') {
    return [f.supplier && `From: ${f.supplier}`, f.document_date && `Date: ${f.document_date}`,
      f.total_amount && `Amount: €${f.total_amount}`, f.summary].filter(Boolean).join('\n') || null;
  }
  return 'Added by scanning the document.';
};

export const ScanDocument = ({ vehicles = [], vehicleId = '', onClose, onSaved }) => {
  const [step, setStep] = useState('pick');          // pick | reading | review
  const [vid, setVid] = useState(vehicleId || '');
  const [upload, setUpload] = useState(null);        // { url, name, isImage }
  const [type, setType] = useState('other');
  const [form, setForm] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const [readError, setReadError] = useState('');
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const vehicle = vehicles.find(v => v.id === vid);
  const lockedVehicle = !!vehicleId;

  const handleFile = async (file) => {
    if (!file) return;
    if (!vid) { toast.error('Choose the vehicle first'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`That file is too large (${MAX_MB} MB maximum)`); return; }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) { toast.error('Please choose a photo (JPG, PNG) or a PDF'); return; }

    setStep('reading');
    setReadError('');
    let up;
    try {
      const r = await uploadFile(file, 'scans');
      up = { url: r.url, name: file.name, isImage };
      setUpload(up);
    } catch (e) {
      toast.error('Upload failed: ' + e.message);
      setStep('pick');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('scan-document', { body: { fileUrl: up.url } });
      if (error || data?.error || !data?.suggestion) {
        throw new Error(data?.error || error?.message || 'Could not read the document');
      }
      const s = data.suggestion;
      const t = TYPES[s.doc_type] ? s.doc_type : 'other';
      const f = {};
      Object.entries(s.fields || {}).forEach(([k, v]) => { f[k] = String(v); });
      setSuggestion(s);
      setType(t);
      setForm(f);
    } catch (e) {
      setReadError(e.message || 'Could not read the document');
      setSuggestion(null);
      setType('other');
      setForm({});
    }
    setStep('review');
  };

  // Does the registration printed on the document match the vehicle it is being filed against?
  const docReg = normReg(suggestion?.registration_number);
  const mismatch = docReg && vehicle && normReg(vehicle.registration_number) !== docReg;
  const matchingVehicle = mismatch ? vehicles.find(v => normReg(v.registration_number) === docReg) : null;

  const save = async (e) => {
    e.preventDefault();
    const meta = TYPES[type];
    if (!vid) { toast.error('Choose a vehicle'); return; }
    if ((type === 'insurance' || type === 'road_tax') && !form.expiry_date) { toast.error('Please enter an expiry date'); return; }
    if (type === 'mot' && !form.result) { toast.error('Please choose the result'); return; }
    if (type === 'other' && !form.title) { toast.error('Please enter a document name'); return; }

    setSaving(true);
    try {
      if (meta.table) {
        const payload = { vehicle_id: vid, ...PAYLOAD[type](form, upload.url) };
        const { error } = await supabase.from(meta.table).insert(payload).select();
        if (error) throw error;
      }
      // Every scan also appears in the Documents list.
      const { error: docErr } = await supabase.from('documents').insert({
        vehicle_id: vid, name: docName(type, form), doc_type: meta.docType,
        document_url: upload.url, notes: docNotes(type, form),
      }).select();
      if (docErr) {
        toast.error('Saved, but it could not be added to the Documents list: ' + docErr.message);
      } else {
        toast.success('Document saved');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
    setSaving(false);
  };

  const renderField = ([key, label, kind, options, showIf]) => {
    if (showIf && !showIf(form)) return null;
    const value = form[key] ?? '';
    if (kind === 'select') {
      return (
        <div className="form-group" key={key}>
          <label className="form-label">{label}</label>
          <select className="form-input" value={value} onChange={e => set(key, e.target.value)}>
            <option value="">{key === 'result' ? 'Choose…' : 'Not sure'}</option>
            {options.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
      );
    }
    if (kind === 'textarea') {
      return (
        <div className="form-group" key={key} style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">{label}</label>
          <textarea className="form-input" value={value} onChange={e => set(key, e.target.value)} />
        </div>
      );
    }
    return (
      <div className="form-group" key={key}>
        <label className="form-label">{label}</label>
        <input className="form-input" type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
          step={kind === 'number' ? '0.01' : undefined} value={value} onChange={e => set(key, e.target.value)} />
      </div>
    );
  };

  const banner = (tone, children) => (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.5,
      background: tone === 'warn' ? 'rgba(249,115,22,0.10)' : 'var(--surface2)',
      border: `1px solid ${tone === 'warn' ? 'rgba(249,115,22,0.4)' : 'var(--border)'}`,
    }}>
      {tone === 'warn' && <AlertTriangle size={16} style={{ color: '#f97316', flexShrink: 0, marginTop: 2 }} />}
      <div>{children}</div>
    </div>
  );

  const footer = step === 'review' ? (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" form="scan-form" type="submit" disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </>
  ) : (
    <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
  );

  return (
    <Modal title="Scan document" onClose={onClose} size="lg" footer={footer}>
      {step === 'pick' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
            Take a photo of an MOT/KTEO certificate, insurance document, service invoice, road tax receipt or
            registration document. We'll read it and fill in the details for you to check.
          </div>
          {lockedVehicle ? (
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>
              {vehicle ? `${vehicle.registration_number} — ${vehicle.brand} ${vehicle.model}` : ''}
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Vehicle *</label>
              <select className="form-input" value={vid} onChange={e => setVid(e.target.value)}>
                <option value="">Select vehicle…</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="button" disabled={!vid} onClick={() => cameraRef.current?.click()}>
              <Camera size={14} /> Take photo
            </button>
            <button className="btn btn-ghost" type="button" disabled={!vid} onClick={() => fileRef.current?.click()}>
              <Upload size={14} /> Choose photo or PDF
            </button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            JPG, PNG or PDF, up to {MAX_MB} MB. A flat, well-lit photo works best. Nothing is saved until you press Save.
          </div>
        </div>
      )}

      {step === 'reading' && (
        <div style={{ textAlign: 'center', padding: '36px 0' }}>
          <div className="spinner" style={{ margin: '0 auto 14px' }} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>Reading your document…</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>This usually takes a few seconds.</div>
        </div>
      )}

      {step === 'review' && (
        <form id="scan-form" onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {readError && banner('warn', <>We couldn't read this automatically ({readError}). You can still fill in the details by hand and the file will be saved with them.</>)}
          {!readError && suggestion && suggestion.confidence < 60 && banner('warn', <>We're not very sure about this one. Please check every field carefully.</>)}
          {mismatch && banner('warn', (
            <>
              This document shows registration <strong>{suggestion.registration_number}</strong>, but you chose{' '}
              <strong>{vehicle.registration_number}</strong>.
              {matchingVehicle && !lockedVehicle && (
                <> <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }}
                  onClick={() => setVid(matchingVehicle.id)}>Switch to {matchingVehicle.registration_number}</button></>
              )}
              {!matchingVehicle && <> Make sure it belongs to this car before saving.</>}
            </>
          ))}

          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {upload?.isImage ? (
              <a href={upload.url} target="_blank" rel="noreferrer">
                <img src={upload.url} alt="Scanned document" style={{ width: 110, maxHeight: 150, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
              </a>
            ) : upload ? (
              <a href={upload.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)' }}>
                <ExternalLink size={12} /> View uploaded PDF
              </a>
            ) : null}
            <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!lockedVehicle && (
                <div className="form-group">
                  <label className="form-label">Vehicle *</label>
                  <select className="form-input" value={vid} onChange={e => setVid(e.target.value)}>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Document type</label>
                <select className="form-input" value={type} onChange={e => setType(e.target.value)}>
                  {Object.entries(TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="form-grid">
            {TYPES[type].fields.map(renderField)}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            {TYPES[type].table
              ? 'This will be added to the vehicle\'s records and to your Documents list.'
              : 'This will be added to your Documents list.'}
          </div>
        </form>
      )}
    </Modal>
  );
};
